import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	ActivateIntegration,
	ActivateIntegrationSuccess,
	EIntegrationsActions,
	GetPaymentNumberModelsSuccess,
	GetPaymentNumberModelsWithDepartments,
	GetPaymentNumberModelsWithUsers, GetQuickPosIntegration, GetQuickPosIntegrationSuccess,
	PatchIntegration,
	PatchIntegrationSuccess, PatchQuickPosIntegration, PatchQuickPosIntegrationSuccess,
	RemoveIntegration,
	RemoveIntegrationSuccess,
} from '@app/core/store/actions/integrations.actions';
import {TenantService} from '@app/modules/shared/services/tenant.service';
import {Tenant} from '@app/core/models/tenant.model';
import {ActivatedRoute, Router} from '@angular/router';
import {QOLogService} from '@quickorder/angular-web-lib';
import {TranslateService} from '@ngx-translate/core';
import {IntegrationService} from '@app/core/services/api/integration.service';
import {GetIntegrations, SetTenant} from '@app/core/store/actions/department-settings.actions';
import {SnackBar} from '@app/core/services/snackbar.service';
import {AuthService} from '@app/core/services/auth.service';
import {getCurrentState} from '@app/core/helpers';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {IPosIntegrationModel} from '@app/core/models/quickpos-integration.model';

// @ts-ignore
@Injectable()
export class IntegrationsEffects {

	@Effect()
	public onGetPaymentNumberModelsWithUsers$ = this.actions$.pipe(
		ofType<GetPaymentNumberModelsWithUsers>(EIntegrationsActions.GetPaymentNumberModelsWithUsers),
		switchMap((action: GetPaymentNumberModelsWithUsers) => from(this.tenantService.getDatabaseSettingsWithUsers()).pipe(
			switchMap((tenant: Tenant) => [
				new GetPaymentNumberModelsSuccess(tenant, tenant.users),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetPaymentNumberModelsWithDepartments$ = this.actions$.pipe(
		ofType<GetPaymentNumberModelsWithDepartments>(EIntegrationsActions.GetPaymentNumberModelsWithDepartments),
		switchMap((action: GetPaymentNumberModelsWithDepartments) => from(this.tenantService.getDatabaseSettingsWithDepartments()).pipe(
			switchMap((tenant: Tenant) => [
				new GetPaymentNumberModelsSuccess(tenant, tenant.departments),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onActivateIntegration$ = this.actions$.pipe(
		ofType<ActivateIntegration>(EIntegrationsActions.ActivateIntegration),
		switchMap((action: ActivateIntegration) => from(this.tenantService.patchDatabaseSettings({integration_id: action.integration.id})).pipe(
			switchMap((res: any) => {
				this.snackBar.newSuccess('NOW_YOU_CAN_USE_X_INTEGRATION', action.integration.name);
				this.router.navigate(['/home/afdelingsindstillinger/integrations/settings']);

				const currentTenant = getCurrentState(this.store).departmentSettings.tenant;
				const tenant = this.tenantService.fresh(currentTenant);
				tenant.integration_id = action.integration.id;

				return [
					new SetTenant(tenant),
					new ActivateIntegrationSuccess(action.integration),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onRemoveIntegration$ = this.actions$.pipe(
		ofType<RemoveIntegration>(EIntegrationsActions.RemoveIntegration),
		switchMap((action: RemoveIntegration) => from(this.tenantService.patchDatabaseSettings({integration_id: ''})).pipe(
			switchMap((res: any) => {
				this.snackBar.newSuccess('INTEGRATIONS_REMOVED');

				const currentTenant = getCurrentState(this.store).departmentSettings.tenant;
				const tenant = this.tenantService.fresh(currentTenant);
				tenant.integration_id = undefined;

				return [
					new SetTenant(tenant),
					new GetIntegrations(),
					new RemoveIntegrationSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPatchIntegration$ = this.actions$.pipe(
		ofType<PatchIntegration>(EIntegrationsActions.PatchIntegration),
		switchMap((action: PatchIntegration) => from(this.tenantService.saveIntegrations(action.paymentNumberModels, action.paymentNumberModel)).pipe(
			switchMap((res: any) => {
				if (action.redirect) {
					this.router.navigate(['/home/afdelingsindstillinger/integrations']);

					this.snackBar.newSuccess('SNACKBAR.NOW_READY_TO_EXPORT');
				}

				return [
					new PatchIntegrationSuccess(),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetQuickPosIntegration$ = this.actions$.pipe(
		ofType<GetQuickPosIntegration>(EIntegrationsActions.GetQuickPosIntegration),
		switchMap((action: GetQuickPosIntegration) => from(this.integrationService.getQuickPosIntegration()).pipe(
			switchMap((integration: IPosIntegrationModel) =>
				[
					new GetQuickPosIntegrationSuccess(integration),
				]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPatchQuickPosIntegration$ = this.actions$.pipe(
		ofType<PatchQuickPosIntegration>(EIntegrationsActions.PatchQuickPosIntegration),
		switchMap((action: PatchQuickPosIntegration) => from(this.integrationService.patchQuickPosIntegration(action.integration)).pipe(
			switchMap((integration: IPosIntegrationModel) => {

					this.route.queryParams.subscribe(c => {
						const params = {...c};
						delete params.auth_token;
						this.router.navigate([], {relativeTo: this.route, queryParams: params});
					}).unsubscribe();
					return [
						new PatchQuickPosIntegrationSuccess(integration),
					];
				}
			),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private tenantService: TenantService,
		private integrationService: IntegrationService,
		private router: Router,
		private logService: QOLogService,
		private snackBar: SnackBar,
		private store: Store<IAppState>,
		public authService: AuthService,
		private route: ActivatedRoute,
	) {
	}
}
